import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { employeesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { DepartmentBadges } from '../components/DepartmentBadges';
import EmployeeApprovalChainSection from '../components/EmployeeApprovalChainSection';
import { EmployeePendingProposals } from '../components/EmployeePendingProposals';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Building2,
  Calendar,
  CreditCard,
  Shield,
  User,
  AlertTriangle,
  FileText,
  GraduationCap,
  ExternalLink,
  Plus,
  MessagesSquare,
  FileSignature,
  UserCheck,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { openAuthFile } from '../lib/openAuthFile';
import { ExpiryBadge, formatDate } from './profile/profileHelpers';
import { CONTRACT_TYPE_LABELS } from './employee/ContractsTab';
import { QuickAddCertDialog } from '../components/QuickAddCertDialog';
import { HireEmployeeDialog } from './employee/HireEmployeeDialog';
import { EmployeeOnboardingCard } from './employee/EmployeeOnboardingCard';

/**
 * Inline-Mini-Komponente für Dokumenten-Anzeige/-Download (analog zu „Mein Profil")
 */
const DocLinks = ({ url, filename, testIdPrefix }) => {
  if (!url) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 mt-2">
      <button
        type="button"
        onClick={() => openAuthFile(url, { filename })
          .catch((err) => toast.error(`Anzeigen fehlgeschlagen: ${err.message || err}`))}
        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        data-testid={`${testIdPrefix}-open`}
      >
        <ExternalLink className="h-3 w-3" />
        {filename || 'Nachweis anzeigen'}
      </button>
      <button
        type="button"
        onClick={() => openAuthFile(url, { filename: filename || 'nachweis.pdf', forceDownload: true })
          .catch((err) => toast.error(`Download fehlgeschlagen: ${err.message || err}`))}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary hover:underline"
        data-testid={`${testIdPrefix}-download`}
      >
        Herunterladen
      </button>
    </div>
  );
};

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, hasPermission } = useAuth();
  const canViewSensitive = isAdmin || hasPermission('sensitive_data', 'view');
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickAdd, setQuickAdd] = useState({ open: false, kind: 'training' });
  const [hireOpen, setHireOpen] = useState(false);
  const canEdit = isAdmin || hasPermission('employees', 'edit');
  // Gespraechsnotizen: SL/NL/Admin haben Zugriff (employees:view oder :edit).
  // Selbstansicht (eigener MA) wird vom Backend blockiert, aber wir blenden
  // den Quick-Button vorsorglich aus, wenn nur view auf eigene Daten gilt.
  const canViewNotes = canEdit || hasPermission('employees', 'view');

  const loadEmployee = useCallback(async () => {
    try {
      const response = await employeesAPI.getOne(id);
      setEmployee(response.data);
    } catch (error) {
      toast.error('Mitarbeiter nicht gefunden');
      navigate('/employees');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadEmployee();
  }, [loadEmployee]);

  const handleDelete = async () => {
    if (!window.confirm(`Möchten Sie "${employee.first_name} ${employee.last_name}" wirklich löschen?`)) return;
    
    try {
      await employeesAPI.delete(id);
      toast.success('Mitarbeiter gelöscht');
      navigate('/employees');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd.MM.yyyy', { locale: de });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-[400px] rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  if (!employee) return null;

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="employee-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/employees')} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-heading font-bold text-3xl text-foreground">
                  {[employee.last_name, employee.first_name].filter(Boolean).join(', ')}
                </h1>
                <Badge
                  variant={employee.is_active ? 'default' : 'secondary'}
                  className={employee.is_active ? 'badge-success' : ''}
                >
                  {employee.is_active ? 'Aktiv' : 'Inaktiv'}
                </Badge>
                {employee.onboarding_status === 'anwaerter' && (
                  <Badge
                    variant="outline"
                    className="gap-1 border-amber-400 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30"
                    data-testid="anwaerter-badge"
                  >
                    <UserCheck className="h-3 w-3" />
                    Anwärter
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground mt-1 font-mono">{employee.employee_number}</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {canEdit && employee.onboarding_status === 'anwaerter' && (
              <Button
                variant="default"
                className="rounded-full badge-success"
                onClick={() => setHireOpen(true)}
                data-testid="hire-employee-btn"
              >
                <UserCheck className="mr-2 h-4 w-4" />
                Als Mitarbeiter übernehmen
              </Button>
            )}
            {canViewNotes && (
              <Button
                variant="default"
                className="rounded-full"
                onClick={() => navigate(`/employees/${id}/edit?tab=notes`)}
                data-testid="quick-note-employee-btn"
              >
                <MessagesSquare className="mr-2 h-4 w-4" />
                Gesprächsnotiz
              </Button>
            )}
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => navigate(`/employees/${id}/edit`)}
              data-testid="edit-employee-btn"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Bearbeiten
            </Button>
            <Button
              variant="destructive"
              className="rounded-full"
              onClick={handleDelete}
              data-testid="delete-employee-btn"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Löschen
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* On-Boarding-Fortschritt (nur bei laufendem Workflow) */}
            <EmployeeOnboardingCard employeeId={id} canEdit={canEdit} />

            {/* Contact Info */}
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Kontaktdaten
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">E-Mail</p>
                      <a href={`mailto:${employee.email}`} className="text-primary hover:underline">
                        {employee.email}
                      </a>
                    </div>
                  </div>
                  {employee.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Telefon</p>
                        <a href={`tel:${employee.phone}`} className="hover:underline">{employee.phone}</a>
                      </div>
                    </div>
                  )}
                  {employee.mobile && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Mobil</p>
                        <a href={`tel:${employee.mobile}`} className="hover:underline">{employee.mobile}</a>
                      </div>
                    </div>
                  )}
                  {employee.date_of_birth && (
                    <div className="flex items-center gap-3">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Geburtsdatum</p>
                        <p>{formatDate(employee.date_of_birth)}</p>
                      </div>
                    </div>
                  )}
                </div>

                {(employee.street || employee.city) && (
                  <>
                    <Separator />
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-sm text-muted-foreground">Adresse</p>
                        <p>
                          {employee.street} {employee.house_number}<br />
                          {employee.postal_code} {employee.city}<br />
                          {employee.country}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Employment Info */}
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Beschäftigung
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground">Einsatz als</p>
                    <p className="font-medium">{employee.position || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Maßnahme/Abteilung</p>
                    <div className="font-medium" data-testid="employee-detail-departments">
                      <DepartmentBadges
                        text={employee.department}
                        size="md"
                        testIdPrefix="employee-detail-dept"
                      />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Hauptstandort</p>
                    <p className="font-medium">{employee.location_name || '-'}</p>
                  </div>
                  {employee.additional_location_names && employee.additional_location_names.length > 0 && (
                    <div>
                      <p className="text-sm text-muted-foreground">Zusätzliche Standorte</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {employee.additional_location_names.map((name) => (
                          <Badge key={name} variant="secondary" className="text-xs">
                            {name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground">Eintrittsdatum</p>
                    <p className="font-medium">{formatDate(employee.employment_start)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Beschäftigungsart</p>
                    <p className="font-medium">{employee.employment_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Wochenstunden</p>
                    <p className="font-medium">{employee.weekly_hours}h</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Urlaubstage/Jahr</p>
                    <p className="font-medium">{employee.vacation_days_per_year} Tage</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bank Info */}
            {canViewSensitive && (employee.iban || employee.bank_name) && (
              <Card className="border-border/60">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-primary" />
                    Bankverbindung
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div>
                      <p className="text-sm text-muted-foreground">IBAN</p>
                      <p className="font-mono">{employee.iban || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">BIC</p>
                      <p className="font-mono">{employee.bic || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Bank</p>
                      <p>{employee.bank_name || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Offene Self-Service-Anträge (nur für Vorgesetzte sichtbar) */}
            <EmployeePendingProposals
              employeeId={employee.id}
              employeeName={`${employee.first_name} ${employee.last_name}`}
            />

            {/* Emergency Contact */}
            {employee.emergency_contact?.name && (
              <Card className="border-border/60">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-warning" />
                    Notfallkontakt
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{employee.emergency_contact.name}</p>
                  </div>
                  {employee.emergency_contact.relationship && (
                    <div>
                      <p className="text-sm text-muted-foreground">Beziehung</p>
                      <p>{employee.emergency_contact.relationship}</p>
                    </div>
                  )}
                  {employee.emergency_contact.phone && (
                    <div>
                      <p className="text-sm text-muted-foreground">Telefon</p>
                      <a href={`tel:${employee.emergency_contact.phone}`} className="text-primary hover:underline">
                        {employee.emergency_contact.phone}
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            {employee.notes && (
              <Card className="border-border/60">
                <CardHeader>
                  <CardTitle className="font-heading">Notizen</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap">{employee.notes}</p>
                </CardContent>
              </Card>
            )}

            {/* Urlaubsfreigabe-Kette */}
            <EmployeeApprovalChainSection
              employeeId={employee.id}
              canEdit={isAdmin || hasPermission('employees', 'edit')}
            />
          </div>
        </div>

        {/* Weiterbildung & Qualifikationen — full-width 2-column grid (wie in „Mein Profil") */}
        {(canEdit
          || employee.trainings?.length > 0
          || employee.qualifications?.length > 0
          || employee.certifications?.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Weiterbildungen */}
            <Card className="border-border/60" data-testid="employee-trainings-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <CardTitle className="font-heading flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  Weiterbildung
                </CardTitle>
                {canEdit && (
                  <Button
                    size="sm"
                    onClick={() => setQuickAdd({ open: true, kind: 'training' })}
                    data-testid="add-training-quick-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Neue hinzufügen
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {employee.trainings?.length > 0 ? (
                  employee.trainings.map((training, idx) => {
                    const id = training.id || `training-${idx}`;
                    return (
                      <div key={id} className="p-3 bg-secondary/30 rounded-lg" data-testid={`emp-detail-training-${id}`}>
                        <p className="font-medium">{training.title || training.name}</p>
                        {training.provider && (
                          <p className="text-sm text-muted-foreground">{training.provider}</p>
                        )}
                        <div className="flex flex-wrap gap-2 mt-1">
                          {(training.completed_date || training.date) && (
                            <Badge variant="outline" className="text-xs">
                              Abgeschlossen: {formatDate(training.completed_date || training.date)}
                            </Badge>
                          )}
                          {training.expiry_date && (
                            <ExpiryBadge expiry={training.expiry_date} />
                          )}
                          {training.hours && (
                            <Badge variant="outline" className="text-xs">{training.hours} h</Badge>
                          )}
                        </div>
                        {training.description && (
                          <p className="text-sm text-muted-foreground mt-1 italic">„{training.description}"</p>
                        )}
                        <DocLinks
                          url={training.certificate_url}
                          filename={training.certificate_filename}
                          testIdPrefix={`emp-detail-training-${id}`}
                        />
                      </div>
                    );
                  })
                ) : (
                  <p className="text-muted-foreground text-center py-4">Keine Weiterbildungen hinterlegt</p>
                )}
                {employee.certifications?.length > 0 && (
                  <div className="pt-2 border-t">
                    <p className="text-sm text-muted-foreground mb-2">Zusätzliche Kenntnisse & Fähigkeiten</p>
                    <div className="flex flex-wrap gap-2">
                      {employee.certifications.map((cert, i) => (
                        <Badge key={`cert-${cert}-${i}`} className="badge-success">{cert}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Qualifications */}
            <Card className="border-border/60" data-testid="employee-qualifications-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <CardTitle className="font-heading flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-primary" />
                  Qualifikationen
                </CardTitle>
                {canEdit && (
                  <Button
                    size="sm"
                    onClick={() => setQuickAdd({ open: true, kind: 'qualification' })}
                    data-testid="add-qualification-quick-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Neue hinzufügen
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {employee.qualifications?.length > 0 ? (
                  employee.qualifications.map((qual, i) => {
                    const isObj = typeof qual === 'object' && qual !== null;
                    const id = isObj ? (qual.id || `qual-${i}`) : `legacy-${i}`;
                    const name = isObj ? qual.name : qual;
                    if (!isObj) {
                      return (
                        <Badge key={id} variant="secondary" data-testid={`emp-detail-qual-${id}`}>{name}</Badge>
                      );
                    }
                    return (
                      <div key={id} className="p-3 bg-secondary/30 rounded-lg" data-testid={`emp-detail-qual-${id}`}>
                        <p className="font-medium">{name}</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted-foreground mt-1">
                          {qual.institution && <span>{qual.institution}</span>}
                          {qual.date && <span>Erworben: {formatDate(qual.date)}</span>}
                        </div>
                        {qual.expiry_date && (
                          <div className="mt-1">
                            <ExpiryBadge expiry={qual.expiry_date} />
                          </div>
                        )}
                        <DocLinks
                          url={qual.document_url}
                          filename={qual.document_filename}
                          testIdPrefix={`emp-detail-qual-${id}`}
                        />
                      </div>
                    );
                  })
                ) : (
                  <p className="text-muted-foreground text-center py-4">Keine Qualifikationen hinterlegt</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Verträge — nur für Nutzer mit Zugriff auf sensible Daten */}
        {canViewSensitive && employee.contracts?.length > 0 && (
          <Card className="border-border/60" data-testid="employee-contracts-card">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading flex items-center gap-2">
                <FileSignature className="h-5 w-5 text-primary" />
                Arbeitsverträge & Ergänzungen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {employee.contracts.map((c, idx) => {
                const id = c.id || `contract-${idx}`;
                return (
                  <div key={id} className="p-3 bg-secondary/30 rounded-lg" data-testid={`emp-detail-contract-${id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">{CONTRACT_TYPE_LABELS[c.contract_type] || 'Vertrag'}</Badge>
                      <p className="font-medium">{c.title}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {c.valid_from && <Badge variant="outline" className="text-xs">Gültig ab: {formatDate(c.valid_from)}</Badge>}
                      {c.valid_until && <ExpiryBadge expiry={c.valid_until} prefix="Befristet bis" />}
                    </div>
                    {c.notes && <p className="text-sm text-muted-foreground mt-1 italic">„{c.notes}"</p>}
                    <DocLinks url={c.document_url} filename={c.document_filename} testIdPrefix={`emp-detail-contract-${id}`} />
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}


        <QuickAddCertDialog
          open={quickAdd.open}
          onOpenChange={(v) => setQuickAdd((p) => ({ ...p, open: v }))}
          kind={quickAdd.kind}
          employee={employee}
          onSuccess={loadEmployee}
        />

        <HireEmployeeDialog
          open={hireOpen}
          onOpenChange={setHireOpen}
          employee={employee}
          onSuccess={loadEmployee}
        />
      </div>
    </DashboardLayout>
  );
}
